import { useState } from "react";
import { X, CheckCircle, XCircle, Send, Building, Briefcase, Globe } from "lucide-react";
import { toast } from "sonner";
import {
  type AccessRequestItem,
  approveAdminAccessRequest,
  rejectAdminAccessRequest,
  resendAdminInvitation,
} from "../../../api/accessRequestsApi";

interface AccessRequestDrawerProps {
  request: AccessRequestItem | null;
  isOpen: boolean;
  onClose: () => void;
  onRequestUpdated: () => void;
}

export default function AccessRequestDrawer({
  request,
  isOpen,
  onClose,
  onRequestUpdated,
}: AccessRequestDrawerProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [assignedRole, setAssignedRole] = useState("client");
  const [assignedPlan, setAssignedPlan] = useState("starter");

  if (!isOpen || !request) return null;

  const handleApprove = async () => {
    if (!confirm(`Are you sure you want to approve access for ${request.full_name} (${request.email})? A secure, single-use invitation email will be generated and dispatched.`)) {
      return;
    }

    setIsProcessing(true);
    try {
      await approveAdminAccessRequest(request.id, {
        assigned_role: assignedRole,
        assigned_plan: assignedPlan,
      });
      toast.success(`Access request for ${request.full_name} approved! Invitation email sent.`);
      onRequestUpdated();
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to approve access request.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await rejectAdminAccessRequest(request.id, rejectReason.trim() || undefined);
      toast.success(`Access request for ${request.full_name} rejected.`);
      setShowRejectInput(false);
      setRejectReason("");
      onRequestUpdated();
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to reject access request.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResend = async () => {
    setIsProcessing(true);
    try {
      await resendAdminInvitation(request.id);
      toast.success(`Fresh invitation email dispatched to ${request.email}.`);
      onRequestUpdated();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } }; message?: string };
      toast.error(axiosErr.response?.data?.detail || axiosErr.message || "Failed to resend invitation.");
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <span className="status-badge active"><CheckCircle size={12} /> Approved</span>;
      case "rejected":
        return <span className="status-badge suspended"><XCircle size={12} /> Rejected</span>;
      default:
        return <span className="status-badge pending">Pending Review</span>;
    }
  };

  const formatDate = (isoString?: string) => {
    if (!isoString) return "N/A";
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  };

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="user-drawer open" id="access-request-drawer">
        {/* Drawer Header */}
        <div className="drawer-header">
          <div className="drawer-header-left">
            <h2 className="drawer-title">{request.full_name}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{request.email}</span>
              {getStatusBadge(request.status)}
            </div>
          </div>
          <button className="drawer-close-btn" onClick={onClose} title="Close drawer">
            <X size={18} />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="drawer-body" style={{ padding: "20px 24px" }}>
          {/* Organization & Designation Card */}
          <div style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--color-border)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--color-text-muted)", marginBottom: 12 }}>
              Organization & Role
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-muted)", marginBottom: 2 }}>
                  <Building size={13} /> Organization
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text)" }}>
                  {request.company}
                </div>
              </div>

              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-muted)", marginBottom: 2 }}>
                  <Briefcase size={13} /> Job Title / Role
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text)" }}>
                  {request.role}
                </div>
              </div>
            </div>

            {request.website && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-muted)", marginBottom: 2 }}>
                  <Globe size={13} /> Website
                </div>
                <a
                  href={request.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 13, color: "#10b981", textDecoration: "underline" }}
                >
                  {request.website}
                </a>
              </div>
            )}
          </div>

          {/* Statement of Need / Reason */}
          <div style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--color-border)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--color-text-muted)", marginBottom: 8 }}>
              Reason for Requesting Access
            </div>
            <p style={{ margin: 0, fontSize: 13, lineHeight: "20px", color: "var(--color-text)", whiteSpace: "pre-wrap" }}>
              {request.reason}
            </p>
          </div>

          {/* Timeline & Lifecycle Tracking */}
          <div style={{ background: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--color-border)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "var(--color-text-muted)", marginBottom: 12 }}>
              Lifecycle & Audit Metadata
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--color-text-muted)" }}>Submitted Date:</span>
                <span style={{ fontWeight: 500 }}>{formatDate(request.created_at)}</span>
              </div>

              {request.reviewed_at && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)" }}>Reviewed Date:</span>
                  <span style={{ fontWeight: 500 }}>{formatDate(request.reviewed_at)}</span>
                </div>
              )}

              {request.reviewer_email && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)" }}>Reviewer:</span>
                  <span style={{ fontWeight: 500 }}>{request.reviewer_email}</span>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--color-text-muted)" }}>Invitation Status:</span>
                <span style={{ fontWeight: 600, color: request.invitation_status === "accepted" ? "#10b981" : request.invitation_status === "sent" ? "#38bdf8" : "#94a3b8" }}>
                  {request.invitation_status.toUpperCase()}
                </span>
              </div>

              {request.invitation_expires_at && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--color-text-muted)" }}>Token Expiry:</span>
                  <span style={{ fontWeight: 500 }}>{formatDate(request.invitation_expires_at)}</span>
                </div>
              )}

              {request.rejection_reason && (
                <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  <span style={{ color: "#ef4444", fontWeight: 600, fontSize: 12 }}>Rejection Reason:</span>
                  <p style={{ margin: "4px 0 0 0", color: "#f87171", fontSize: 13 }}>{request.rejection_reason}</p>
                </div>
              )}
            </div>
          </div>

          {/* Pending Approval Options */}
          {request.status === "pending" && (
            <div style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.2)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#10b981", marginBottom: 12 }}>
                Pre-Assignment Configuration
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                    Role Assignment
                  </label>
                  <select
                    className="select-filter"
                    value={assignedRole}
                    onChange={(e) => setAssignedRole(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="client">Client</option>
                    <option value="analyst">Analyst</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: 12, color: "var(--color-text-muted)", display: "block", marginBottom: 4 }}>
                    Initial Tier Plan
                  </label>
                  <select
                    className="select-filter"
                    value={assignedPlan}
                    onChange={(e) => setAssignedPlan(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Rejection Note Form */}
          {showRejectInput && request.status === "pending" && (
            <div style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.25)", borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#ef4444", display: "block", marginBottom: 6 }}>
                Rejection Reason (Internal Record / Audit):
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Incomplete organizational details, unverified domain, or duplicate submission..."
                rows={3}
                style={{
                  width: "100%",
                  background: "#0d1117",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  color: "#f8fafc",
                  padding: "8px 12px",
                  fontSize: 13,
                  resize: "vertical",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
                <button
                  className="btn-secondary"
                  onClick={() => setShowRejectInput(false)}
                  disabled={isProcessing}
                  style={{ padding: "6px 12px", fontSize: 12 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isProcessing}
                  style={{
                    padding: "6px 14px",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "#ef4444",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Drawer Actions Footer */}
        <div className="drawer-footer" style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "16px 24px", borderTop: "1px solid var(--color-border)" }}>
          {request.status === "pending" && !showRejectInput && (
            <>
              <button
                className="btn-secondary"
                onClick={() => setShowRejectInput(true)}
                disabled={isProcessing}
                style={{ color: "#ef4444" }}
              >
                <XCircle size={15} /> Reject
              </button>
              <button
                className="btn-primary"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                <CheckCircle size={15} /> Approve & Issue Invitation
              </button>
            </>
          )}

          {request.status === "approved" && request.invitation_status !== "accepted" && (
            <button
              className="btn-secondary"
              onClick={handleResend}
              disabled={isProcessing}
            >
              <Send size={15} /> Resend Invitation Email
            </button>
          )}

          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}
